import { LinkablePicker } from '@Components/LinkablePicker';
import { STAMMDATEN_API } from '@Constants/backendConstants';
import { addressColumns, containerColumns, containerTypeColumns, serviceColumns } from '@Constants/TableColumns';
import {
    onlyCollectiveTypes,
    onlyContainersOfType,
    onlyCustomers,
    onlyIndividualTypes,
    onlyMaterials,
} from '@Hooks/Stammdaten/linkFilters';
import { useListApi } from '@Hooks/useListApi';
import {
    type ApiAddress,
    type ApiContainer,
    type ApiContainerType,
    type ApiService,
    EnumFrontendLayoutSetting,
    type LinkableField,
} from '@Model';
import {
    AddressesRepository,
    ContainersRepository,
    ContainerTypesRepository,
    ServicesRepository,
} from '@Repositories/Stammdaten';
import {
    Button,
    Card,
    DateInputBase,
    FlexRow,
    Grid,
    GridColumn,
    GridColumns,
    type ListRequest,
    Modal,
    TextInputBase,
    useTranslate,
} from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';
import { type ContainerStockRow } from './containerStockGroups';
import {
    type ContainerStockDraft,
    draftFromRow,
    emptyDraft,
    entryFromDraft,
    isPoolRow,
    missingFields,
    StockResource,
    withContainerType,
} from './containerStockCorrection';
import { useContainerStockCorrection } from './useContainerStockCorrection';

/** Fixed, because the modal sizes itself to its content. */
const BODY_WIDTH = '720px';

const sortByNumber = { columnName: 'number', direction: 'asc' } as const;

interface ContainerStockCorrectionModalProps {
    /** The standing time to correct; absent when a container is added to the stock. */
    row: ContainerStockRow | undefined;
    onClose: () => void;
    /** Called once the line is saved, so the stock can be read again. */
    onSaved: () => void;
}

/**
 * Sets a standing time to what is true in the yard, or adds a container that stands without a
 * booked action.
 */
// @ai-generated 2026-10-02T08:10:14+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function ContainerStockCorrectionModal({ row, onClose, onSaved }: ContainerStockCorrectionModalProps) {
    const i18n = useTranslate('');
    const pool = isPoolRow(row);
    const [draft, setDraft] = useState<ContainerStockDraft>(() => (row ? draftFromRow(row) : emptyDraft(new Date())));

    const { loadPage: loadAddresses } = useListApi<ApiAddress>(AddressesRepository, STAMMDATEN_API);
    const { loadPage: loadServices } = useListApi<ApiService>(ServicesRepository, STAMMDATEN_API);
    const { loadPage: loadContainers } = useListApi<ApiContainer>(ContainersRepository, STAMMDATEN_API);
    const { loadPage: loadContainerTypes } = useListApi<ApiContainerType>(ContainerTypesRepository, STAMMDATEN_API);

    const loadCustomers = useCallback((request: ListRequest) => loadAddresses(onlyCustomers(request)), [loadAddresses]);
    const loadMaterials = useCallback((request: ListRequest) => loadServices(onlyMaterials(request)), [loadServices]);
    const loadTypes = useCallback(
        (request: ListRequest) => loadContainerTypes((pool ? onlyCollectiveTypes : onlyIndividualTypes)(request)),
        [loadContainerTypes, pool],
    );
    const typeId = draft.containerType.foreign_key;
    const loadContainersOfType = useCallback(
        (request: ListRequest) => loadContainers(typeId ? onlyContainersOfType(request, typeId) : request),
        [loadContainers, typeId],
    );

    const { save, isSaving } = useContainerStockCorrection(onSaved);

    const change = useCallback((changes: Partial<ContainerStockDraft>) => {
        setDraft((current) => ({ ...current, ...changes }));
    }, []);
    const pick = (field: 'containerType' | 'billedAs' | 'site' | 'customer' | 'content') => (link: LinkableField) =>
        change({ [field]: link });

    const missing = missingFields(draft, pool);
    const isMissing = (field: string) => missing.includes(field);

    const submit = useCallback(async () => {
        const saved = await save(entryFromDraft(draft, row), row?.standing_time_id);
        if (saved) {
            onClose();
        }
    }, [save, draft, row, onClose]);

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={row ? i18n('Bestand korrigieren') : i18n('Container zum Bestand hinzufügen')}
            footer={
                <FlexRow justify="center" gap="sm" className="pt-5">
                    <Button
                        appearance="outlined"
                        variant="normal"
                        onClick={onClose}
                        locked={isSaving}
                        label="Abbrechen"
                    >
                        {i18n('Abbrechen')}
                    </Button>
                    <Button
                        onClick={() => void submit()}
                        locked={isSaving || missing.length > 0}
                        tooltip={missing.length > 0 ? `${i18n('Es fehlt noch')}: ${missing.join(', ')}` : undefined}
                    >
                        {i18n('Speichern')}
                    </Button>
                </FlexRow>
            }
        >
            <Card
                name="stock_correction"
                border="withoutBorder"
                padding="none"
                collapsable={false}
                maxWidth={BODY_WIDTH}
            >
                <Grid columns={12}>
                    <GridColumns spans={[6, 6]}>
                        <LinkablePicker<ApiContainerType>
                            name="stock_container_type"
                            label={i18n('Containertyp')}
                            link={draft.containerType}
                            resource={StockResource.ContainerType}
                            layoutSettingsContext={EnumFrontendLayoutSetting.ContainerTypesTableLayout}
                            columns={containerTypeColumns}
                            load={loadTypes}
                            onPick={(containerType) => setDraft((current) => withContainerType(current, containerType))}
                            required={isMissing('Containertyp')}
                        />
                        <LinkablePicker<ApiContainer>
                            name="stock_container"
                            label={i18n('Nummer')}
                            link={draft.container}
                            resource={StockResource.Container}
                            layoutSettingsContext={EnumFrontendLayoutSetting.ContainersTableLayout}
                            columns={containerColumns}
                            load={loadContainersOfType}
                            onPick={(container, record) =>
                                change({
                                    container,
                                    containerType: record?.type?.foreign_key
                                        ? { ...record.type, api_resource_name: StockResource.ContainerType }
                                        : draft.containerType,
                                })
                            }
                            placeholder={pool ? i18n('ohne Nummer') : undefined}
                            sorting={sortByNumber}
                            locked={pool}
                            required={isMissing('Nummer')}
                        />
                    </GridColumns>

                    <GridColumns spans={[6, 6]}>
                        <LinkablePicker<ApiContainerType>
                            name="stock_billed_as"
                            label={i18n('Abgerechnet als')}
                            link={draft.billedAs}
                            resource={StockResource.ContainerType}
                            layoutSettingsContext={EnumFrontendLayoutSetting.ContainerTypesTableLayout}
                            columns={containerTypeColumns}
                            load={loadContainerTypes}
                            onPick={pick('billedAs')}
                        />
                        <LinkablePicker<ApiService>
                            name="stock_content"
                            label={i18n('Inhalt')}
                            link={draft.content}
                            resource={StockResource.Service}
                            layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                            columns={serviceColumns}
                            load={loadMaterials}
                            onPick={pick('content')}
                        />
                    </GridColumns>

                    <GridColumns spans={[6, 6]}>
                        <LinkablePicker<ApiAddress>
                            name="stock_site"
                            label={i18n('Standort')}
                            link={draft.site}
                            resource={StockResource.Address}
                            layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                            columns={addressColumns}
                            load={loadAddresses}
                            onPick={pick('site')}
                            required={isMissing('Standort')}
                        />
                        <LinkablePicker<ApiAddress>
                            name="stock_customer"
                            label={i18n('Kunde')}
                            link={draft.customer}
                            resource={StockResource.Address}
                            layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                            columns={addressColumns}
                            load={loadCustomers}
                            onPick={pick('customer')}
                            required={isMissing('Kunde')}
                        />
                    </GridColumns>

                    <GridColumn span={12}>
                        <TextInputBase
                            name="stock_remarks"
                            label={i18n('Bemerkung')}
                            value={draft.remarks}
                            onChange={(remarks) => {
                                change({ remarks });
                                return true;
                            }}
                        />
                    </GridColumn>

                    <GridColumns spans={[6, 6]}>
                        <DateInputBase
                            name="stock_standing_since"
                            label={i18n('Steht seit')}
                            type="datetime"
                            value={draft.standingSince}
                            required={isMissing('Steht seit')}
                            onChange={(standingSince) => {
                                change({ standingSince });
                                return true;
                            }}
                        />
                        <DateInputBase
                            name="stock_ended_at"
                            label={i18n('Abgeholt am')}
                            type="datetime"
                            clearable
                            minDate={draft.standingSince === '' ? undefined : new Date(draft.standingSince)}
                            value={draft.endedAt}
                            onChange={(endedAt) => {
                                change({ endedAt });
                                return true;
                            }}
                        />
                    </GridColumns>
                </Grid>
            </Card>
        </Modal>
    );
}
